import { useRef, useState } from 'react';

import { Button, showSnackbar } from 'fluxo-ui';
import { CheckIcon, CopyIcon, TrashIcon, UploadIcon } from 'fluxo-ui/icons';

import mediaService from '../../services/media.service';

interface MediaFile {
    id: string;
    name: string;
    customId: string;
    type: 'image' | 'document' | 'video' | 'audio';
    url: string;
    size: number;
    uploading?: boolean;
}

interface MediaUploaderProps {
    files?: MediaFile[];
    onChange?: (files: MediaFile[]) => void;
    /** When editing an existing question, uploads are attached to it in the media library. */
    questionId?: string;
    maxFiles?: number;
    maxSizeBytes?: number;
    allowedTypes?: string[];
    disabled?: boolean;
}

const MediaUploader: React.FC<MediaUploaderProps> = ({
    files = [],
    onChange,
    questionId,
    maxFiles = 10,
    maxSizeBytes = 10 * 1024 * 1024, // 10MB
    allowedTypes = ['image/*', 'video/*', 'audio/*', 'application/pdf', '.doc', '.docx', '.ppt', '.pptx'],
    disabled = false,
}) => {
    const [uploadedFiles, setUploadedFiles] = useState<MediaFile[]>(files);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        if (!selectedFiles || selectedFiles.length === 0) return;

        if (uploadedFiles.length + selectedFiles.length > maxFiles) {
            showSnackbar(`You can only upload up to ${maxFiles} files`, 'Limit Exceeded', { type: 'warning' });
            return;
        }

        // Reset input so the same file can be re-selected later
        const filesToUpload = Array.from(selectedFiles);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }

        let currentFiles = uploadedFiles;

        for (const file of filesToUpload) {
            // Validate size
            if (file.size > maxSizeBytes) {
                showSnackbar(`File "${file.name}" exceeds ${maxSizeBytes / 1024 / 1024}MB limit`, 'File Too Large', { type: 'error' });
                continue;
            }

            // Determine file type
            let fileType: MediaFile['type'] = 'document';
            if (file.type.startsWith('image/')) fileType = 'image';
            else if (file.type.startsWith('video/')) fileType = 'video';
            else if (file.type.startsWith('audio/')) fileType = 'audio';

            // Create custom ID from filename (without extension)
            const customId = file.name
                .replace(/\.[^/.]+$/, '')
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '_');

            const tempId = `temp-${Date.now()}-${file.name}`;
            const pendingFile: MediaFile = {
                id: tempId,
                name: file.name,
                customId,
                type: fileType,
                url: URL.createObjectURL(file),
                size: file.size,
                uploading: true,
            };

            currentFiles = [...currentFiles, pendingFile];
            setUploadedFiles(currentFiles);

            try {
                const result = await mediaService.uploadQuestionMedia(questionId || '', file, customId);
                currentFiles = currentFiles.map((f) =>
                    f.id === tempId ? { ...f, id: result.mediaId, url: result.url, uploading: false } : f,
                );
                setUploadedFiles(currentFiles);
            } catch (error: any) {
                currentFiles = currentFiles.filter((f) => f.id !== tempId);
                setUploadedFiles(currentFiles);
                showSnackbar(error.message || `Failed to upload "${file.name}"`, 'Upload Failed', { type: 'error' });
            }
        }

        onChange?.(currentFiles.filter((f) => !f.uploading));
    };

    const handleRemove = async (fileId: string) => {
        try {
            if (!fileId.startsWith('temp-')) {
                await mediaService.deleteFile(fileId);
            }
            const updatedFiles = uploadedFiles.filter((f) => f.id !== fileId);
            setUploadedFiles(updatedFiles);
            onChange?.(updatedFiles);
            showSnackbar('File removed', 'Success', { type: 'success' });
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to remove file', 'Error', { type: 'error' });
        }
    };

    const handleCopyPlaceholder = (file: MediaFile) => {
        const placeholder = `{${file.type}:${file.customId}}`;
        navigator.clipboard.writeText(placeholder);
        setCopiedId(file.id);
        showSnackbar(`Copied: ${placeholder}`, 'Placeholder Copied', { type: 'success' });
        setTimeout(() => setCopiedId(null), 2000);
    };

    const formatFileSize = (bytes: number) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const getFileIcon = (type: MediaFile['type']) => {
        return (
            <div
                className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    type === 'image'
                        ? 'bg-teal-100 dark:bg-teal-900'
                        : type === 'video'
                          ? 'bg-purple-100 dark:bg-purple-900'
                          : type === 'audio'
                            ? 'bg-green-100 dark:bg-green-900'
                            : 'bg-orange-100 dark:bg-orange-900'
                }`}
            >
                <span className="text-2xl">{type === 'image' ? '🖼️' : type === 'video' ? '🎥' : type === 'audio' ? '🎵' : '📄'}</span>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            {/* Upload Button */}
            <div>
                <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={allowedTypes.join(',')}
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={disabled || uploadedFiles.length >= maxFiles}
                />
                <Button
                    label={`Upload Media (${uploadedFiles.length}/${maxFiles})`}
                    leftIcon={UploadIcon}
                    variant="default"
                    layout="outlined"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || uploadedFiles.length >= maxFiles}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
                    Supported: Images, Videos, PDFs, Documents (Max {formatFileSize(maxSizeBytes)} per file)
                </p>
            </div>

            {/* Uploaded Files */}
            {uploadedFiles.length > 0 && (
                <div className="space-y-3">
                    <div className="text-sm font-medium text-slate-900 dark:text-white">Uploaded Media</div>
                    <div className="space-y-2">
                        {uploadedFiles.map((file) => (
                            <div
                                key={file.id}
                                className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                            >
                                <div className="flex items-center gap-3 flex-1">
                                    {file.type === 'image' ? (
                                        <img src={file.url} alt={file.name} className="w-12 h-12 object-cover rounded-lg" />
                                    ) : (
                                        getFileIcon(file.type)
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{file.name}</p>
                                            {file.uploading && (
                                                <span className="text-xs text-teal-600 dark:text-teal-400 animate-pulse">Uploading...</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <p className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(file.size)}</p>
                                            <span className="text-xs text-slate-400">•</span>
                                            <code className="text-xs bg-slate-200 dark:bg-slate-700 px-2 py-0.5 rounded">
                                                {`{${file.type}:${file.customId}}`}
                                            </code>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                    <button
                                        onClick={() => handleCopyPlaceholder(file)}
                                        disabled={file.uploading}
                                        className="p-2 text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
                                        title="Copy placeholder"
                                    >
                                        {copiedId === file.id ? (
                                            <CheckIcon className="w-4 h-4 text-green-600" />
                                        ) : (
                                            <CopyIcon className="w-4 h-4" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => handleRemove(file.id)}
                                        disabled={file.uploading || disabled}
                                        className="p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors disabled:opacity-50"
                                        title="Remove file"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Info Box */}
            <div className="p-3 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg">
                <p className="text-sm text-teal-900 dark:text-teal-100 font-medium mb-1">How to use media</p>
                <p className="text-sm text-teal-700 dark:text-teal-300">
                    Copy the placeholder code and paste it into your question text where you want the media to appear. Example:{' '}
                    <code className="bg-teal-100 dark:bg-teal-900 px-1 rounded">{'{image:diagram1}'}</code>
                </p>
            </div>
        </div>
    );
};

export default MediaUploader;
